import type { ProductModifierGroups } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adds extras max quantities to their paired included group so the UI
 * shows the combined limit (e.g. "3/8" instead of "3/3").
 *
 * Works directly from the product's modifier groups — no need for
 * ingredient-level maps.
 */
export function combineMaxLimitsForPairedGroups(
  base: Limits,
  modifierGroups: ProductModifierGroups,
): Limits {
  const includedModificationsGroups = modifierGroups.filter((group) => group.groupRole === 'included'); // prettier-ignore
  const extraModificationsGroups = modifierGroups.filter((group) => group.groupRole === 'extras');
  const extrasGroupsByPairedId = new Map(
    extraModificationsGroups.map((group) => [group.pairedGroupId ?? '', group]),
  );

  return includedModificationsGroups.reduce<Limits>(
    (combined, includedGroup) => {
      const { id, name: includedGroupName } = includedGroup;

      const extrasGroup = extrasGroupsByPairedId.get(id);

      if (!extrasGroup) return combined;

      const { name: extrasGroupName } = extrasGroup;

      const includedGroupMax = base[includedGroupName] ?? 0;
      const extrasGroupMax = base[extrasGroupName] ?? 0;

      return {
        ...combined,
        [includedGroup.name]: includedGroupMax + extrasGroupMax,
      };
    },
    { ...base },
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Limits = Record<string, number>;
